import { formatMoney as shopifyFormatMoney } from '@shopify/theme-currency'
import { formatAsPercentage } from '../helpers/utils'

const settings = window.GM_STATE

const formatMoney = function (cents, format = settings.shopify.moneyFormat) {
  return shopifyFormatMoney(cents, format)
}

const getCustomBadgeClass = (tag) => {
  return tag.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '')
}

const getBadges = (tags) => {
  const items = []

  if (tags.length) {
    for (const tag of tags) {
      if (tag.includes('badge-')) {
        const tagName = tag.replace('badge-', '')

        items.push({
          text: tagName,
          tag,
          customClass: getCustomBadgeClass(tag)
        })
      }
    }
  }

  return items
}

const addBadgeSale = (badges, price, originalPrice) => {
  if (price && originalPrice && price < originalPrice) {
    const salePercent = Math.round((originalPrice - price) / originalPrice * 100)
    const text = `${formatAsPercentage(salePercent)} off`

    badges.unshift({
      text,
      tag: 'badge-sale-off',
      customClass: 'badge-sale-off'
    })
  }

  return badges
}

const addBadgeSoldOut = (badges) => {
  badges.push({
    text: 'Sold Out',
    tag: 'badge-sold-out',
    customClass: 'badge-sold-out'
  })

  return badges
}

const getVariantDisplayTitle = (product) => {
  if (product.namedTags?.variant_display) {
    return product.namedTags.variant_display
  }

  const tagVariantDisplay = product.tags?.find((tag) => tag.includes('variant_display:'))

  return tagVariantDisplay ? tagVariantDisplay.split(':')[1] : null
}

const getVariantCompareAtPrice = (variant) => variant?.compareAtPrice ?? variant?.compare_at_price ?? null

const getDisplayVariant = (product, preferredVariant = null) => {
  const variants = product.variants || []
  const fallback = preferredVariant || variants.find((variant) => variant.available) || variants[0]
  const displayTitle = getVariantDisplayTitle(product)

  if (displayTitle) {
    const match = variants.find((variant) => variant.title === displayTitle)

    if (match) return match
  }

  return fallback
}

const getSaleBadgePrices = (product, preferredVariant = null) => {
  const variant = getDisplayVariant(product, preferredVariant)

  return {
    price: variant?.price ?? product.price,
    compareAtPrice: getVariantCompareAtPrice(variant) ?? product.compareAtPrice
  }
}

const formatRangePrice = (values) => values && values.map(item => formatMoney(item * 100)).join(' - ')

const getDefaultImage = (img) => {
  return (Array.isArray(img) && img.length === 0) || !img ? GM_STATE.shopify.defaultImage : img
}

const getGraphQLID = (id, type) => `gid://shopify/${type}/${id}`

const getGraphQLIds = (ids, type) => {
  if (ids.length === 0) return []

  return ids.map((id) => getGraphQLID(id, type))
}

function convertPrice(price) {
  return typeof price === 'string' ? parseFloat(price) : price
}

function getPrice(price) {
  return price ? convertPrice(price) * 100 : 0
}

function checkShipmentDate (dataShipmentDate) {
  const shipmentDate = dataShipmentDate
  const now = new Date()

  const shipmentDateObj = new Date(shipmentDate + ' ' + now.getFullYear())
  const nowObj = new Date(now)

  return shipmentDateObj < nowObj
}

function rendererRichText(content) {
  if (!content) return ''

  const parsed = JSON.parse(content)
  let html = ''

  parsed.children.forEach((node) => {
    switch (node.type) {
      case 'heading':
        html += `<h${node.level}>${node.children[0].value}</h${node.level}>`
        break
      case 'list':
        html += `<${node.listType === 'unordered' ? 'ul' : 'ol'}>`
        node.children.forEach((item) => {
          html += `<li>${item.children[0].value}</li>`
        })
        html += `<${node.listType === 'unordered' ? '/ul' : '/ol'}>`
        break
      case 'paragraph':
        html += '<p>'
        node.children.forEach((item) => {
          if (item.type === 'text' && item.bold) {
            html += `<strong>${item.value}</strong>` + ' '
          } else if (item.type === 'text' && item.italic) {
            html += `<em>${item.value}</em>` + ' '
          } else if (item.type === 'text') {
            html += `${item.value}` + ' '
          }

          if (item.type === 'link' && item.bold) {
            html +=
              `<a href="${item.url}" target="${item.target || '_self'}"><strong>${item.children[0].value}</strong></a>` +
              ' '
          } else if (item.type === 'link' && item.italic) {
            html +=
              `<a href="${item.url}" target="${item.target || '_self'}"><em>${item.children[0].value}</em></a>` +
              ' '
          } else if (item.type === 'link') {
            html +=
              `<a href="${item.url}" target="${item.target || '_self'}">${item.children[0].value}</a>` + ' '
          }
        })
        html += '</p>'
        break
    }
  })

  return html
}

const getPriceWithDiscount = (price, discount = 0, type = 'percentage') => {
  switch (type) {
    case 'net':
      return price - discount * 100

    default:
      return price - (price * discount) / 100
  }
}

function formatMoneySubscription(value) {
  return formatMoney(value * 100)
}

const convertToDateISO = (dobString) => {
  return new Date(dobString).toISOString()
}

const formatString = (string) => {
  return string
    .split('-')
    .map((word, index) =>
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
    ).join(' ')
}

const getBadgesWithSalesBadge = (target = 'ProductBadge', value) => {
  const currentTags = window[target] || window.ProductBadge || []

  if (currentTags.length) {
    const badges = getBadges(currentTags)

    // add on the sales tag
    if (value) {
      if (value.price && value.compare_at_price && value.price < value.compare_at_price) {
        const salePercent = Math.round((value.compare_at_price - value.price) / value.compare_at_price * 100)
        const text = `${formatAsPercentage(salePercent)} off`

        badges.push({
          text,
          tag: 'badge-sale-off',
          customClass: 'badge-sale-off'
        })
      }
    }

    return badges
  }

  return []
}

function getFreeShippingAmount() {
  const country = window.Shopify.country
  const baseFreeShippingAmount = GM_STATE.cart.thresholdAmount || 0

  const countryAmounts = {
    AU: baseFreeShippingAmount,
    GB: GM_STATE.cart.thresholdAmountUK || 0,
    CA: GM_STATE.cart.thresholdAmountCA || 0,
    US: baseFreeShippingAmount
  }

  return countryAmounts[country] || baseFreeShippingAmount
}

export {
  getBadgesWithSalesBadge,
  addBadgeSale,
  addBadgeSoldOut,
  getDisplayVariant,
  getSaleBadgePrices,
  checkShipmentDate,
  formatMoney,
  formatRangePrice,
  getBadges,
  getCustomBadgeClass,
  getDefaultImage,
  getGraphQLID,
  getGraphQLIds,
  getPrice,
  rendererRichText,
  getPriceWithDiscount,
  formatMoneySubscription,
  convertToDateISO,
  formatString,
  getFreeShippingAmount
}
